import { useTranslation } from "react-i18next";
import { XCircle } from "lucide-react";
import { ConfirmDialog } from "@tellescope/ui";
import { formatDate } from "@tellescope/utils";
import { getStripePlanProductName } from "../../../hooks/useStripeSubscriptions";
import type { StripeSubscription } from "../../../hooks/useStripeSubscriptions";
import { useStripeSubscriptionMutation } from "../../../hooks/useStripeSubscriptionMutations";

interface CancelConfirmDialogProps {
  subscription: StripeSubscription;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function CancelConfirmDialog({ subscription, open, onOpenChange, onSuccess }: CancelConfirmDialogProps) {
  const { t } = useTranslation("payments");
  const cancelMutation = useStripeSubscriptionMutation();

  const { effectivePlan } = subscription;
  const productName = getStripePlanProductName(effectivePlan);
  const endDate = formatDate(subscription.current_period_end * 1000);
  const isTrialing = subscription.status === "trialing";

  const handleConfirm = () => {
    cancelMutation.mutate(
      {
        integration: "Stripe",
        type: "subscription-cancel",
        id: subscription.id,
      },
      { onSuccess }
    );
  };

  const description = isTrialing
    ? productName
      ? t("Your free trial of {{productName}} will end immediately. You will not be charged.", { productName })
      : t("Your free trial will end immediately. You will not be charged.")
    : t("Access continues until {{date}}. You can undo this anytime before then.", { date: endDate });

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      variant="destructive"
      icon={<XCircle className="h-5 w-5" />}
      title={t("Cancel Subscription")}
      description={description}
      confirmText={t("Cancel Subscription")}
      cancelText={t("Keep Subscription")}
      onConfirm={handleConfirm}
      loading={cancelMutation.isPending}
    />
  );
}
